import React from 'react';
import { useBLoC } from 'hooks/useBLoC';
import { useInitBloc } from 'hooks/useInitBloc';
import { BLoCBase, BLoCParams, renderBlocChild } from 'types/BLoCBase';
import {
  combineLatest,
  finalize,
  first,
  map,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { CheckboxChangeEvent } from 'primereact/checkbox';
import { $get, $put } from 'services/api';
import { useObservableState } from 'observable-hooks';
import { getCustomerStore } from 'stores/customer.store';
import { getBillingPlanStore } from 'stores/billingPlan.store';
import { ImportModalCsvTypes } from 'components/modals/ImportModal/components/ImportModalCsv/ImportModalCsv.types';
import {
  TC_IMPORT_COUNT_LIMIT,
  TC_IMPORT_TEXT_RESPONSE_TYPE_ID,
  useImportModalCsvBLoC,
} from 'components/modals/ImportModal/components/ImportModalCsv/ImportModalCsv.bloc';
import { broadcast } from 'services/broadcast';

type Column = ImportModalCsvTypes.IngestionColumn & {
  costData: { colName: string; hasHistoricTc: boolean; realCost: number; count: number };
};

type State = {
  selectedColumnsIds: number[];
  loading?: boolean;
  updateCostDetailsLoading?: boolean;
  columns: Column[];
  baseColumns: ImportModalCsvTypes.IngestionColumn[];
  useLegacyCodeframes?: boolean;
  costDetails?: ImportModalCsvTypes.CostDetails;
};

class BLoC extends BLoCBase<State> {
  public $loading = this.$getState('loading');
  public $updateCostDetailsLoading = this.$getState('updateCostDetailsLoading');
  public $useLegacyCodeframes = this.$getState('useLegacyCodeframes');
  public $columns = this.$getState('columns');
  public $baseColumns = this.$getState('baseColumns');
  public $selectedColumnsIds = this.$getState('selectedColumnsIds');

  public $pipelines = $get<{ id: number; name: string; allowsCodeframe: boolean }[]>(
    'pipelines',
    { pipelineTypeId: 2 },
    { root: '/admin-api/' }
  ).pipe(shareReplay());

  public $adminCodeframes = $get<{ id: number; name: string }[]>(
    'theme-codeframes',
    {},
    { root: '/admin-api/' }
  ).pipe(shareReplay());

  public $customerCodeframes = combineLatest([
    getCustomerStore().$customerSlug.pipe(first((slug) => !!slug)),
    broadcast.$listen('codeframesUpdated').pipe(startWith(1)),
  ]).pipe(
    switchMap(([customerSlug]) =>
      $get<{ id: number; name: string; type: 'thematic' | 'recall' }[]>(
        `ingestions/customers/${customerSlug}/codeframes`,
        {},
        {}
      )
    ),
    shareReplay()
  );

  public $filteredColumn = this.$columns.pipe(
    map((columns) => columns.filter((col) => col.costData.realCost >= TC_IMPORT_COUNT_LIMIT))
  );

  public $isSelectedAll = combineLatest([this.$selectedColumnsIds, this.$filteredColumn]).pipe(
    map(
      ([selectedColumnsIds, columns]) =>
        !!(selectedColumnsIds?.length === columns.length && selectedColumnsIds.length)
    )
  );

  public $selecteColumnsData = combineLatest([this.$columns, this.$selectedColumnsIds]).pipe(
    map(([columnsWithCostDetails, selectedColumns]) =>
      columnsWithCostDetails.filter((col) => selectedColumns.includes(col.id))
    )
  );

  public $costDetails = $get<ImportModalCsvTypes.CostDetails>(
    `ingestions/${this.currentIngestionId}/cost-details`
  ).pipe(
    tap((costDetails) => {
      const columns: ImportModalCsvTypes.IngestionColumn[] = [...this.currentIngestionColumns].map((c) => {
        return {
          ...c,
          extraInfo: {
            ...c.extraInfo,
            ...(c.responseDataTypeId === TC_IMPORT_TEXT_RESPONSE_TYPE_ID && {
              recommendedCodeframeId:
                typeof c.extraInfo.recommendedCodeframeId === 'number'
                  ? c.extraInfo.recommendedCodeframeId
                  : null,
              recommendedPipelineId:
                typeof c.extraInfo.recommendedPipelineId === 'number'
                  ? c.extraInfo.recommendedPipelineId
                  : null,
              tcCodeframeId: typeof c.extraInfo.tcCodeframeId === 'number' ? c.extraInfo.tcCodeframeId : null,
            }),
          },
        };
      });

      const filteredColumns = this.filterTextResponseColumns(columns);

      const columnsWithCostDetails = this.addCostDataToColumns(filteredColumns, costDetails);

      this.setStates({
        baseColumns: columns,
        columns: columnsWithCostDetails,
        costDetails,
        selectedColumnsIds: [
          ...this.currentSelectedColumnIds.filter((col) => {
            const column = columnsWithCostDetails.find((c) => c.id === col);
            return (column?.costData.realCost || 0) >= TC_IMPORT_COUNT_LIMIT;
          }),
        ],
      });
    }),
    first(),
    switchMap(() => this.$getState('costDetails')),
    shareReplay()
  );

  public $costValues = combineLatest([
    this.$costDetails,
    this.$selecteColumnsData,
    getBillingPlanStore().$creditsBalance,
  ]).pipe(
    map(([costDetails, selecteColumnsData, credits = 0]) => {
      const genCost = costDetails?.ingestion.cost || 0;
      const genCount = costDetails?.ingestion.rowsCount || 0;
      const rowsCost = selecteColumnsData.reduce((acc, item) => {
        return acc + this.getColumnRealCost(item.id, costDetails);
      }, 0);
      const rowsCount = selecteColumnsData.reduce((acc, item) => acc + (+item.costData.count || 0), 0);
      const hasCodeFrames = selecteColumnsData.some((col) => !!col.extraInfo?.tcCodeframeId);
      const total = rowsCost + genCost;
      const remaining = credits - total;
      return {
        genCost,
        genCount,
        rowsCost,
        rowsCount,
        credits,
        remaining,
        total,
        hasCodeFrames,
      };
    })
  );

  constructor(
    public readonly currentIngestionId: number,
    public readonly currentIngestionColumns: ImportModalCsvTypes.IngestionColumn[],
    public readonly currentSelectedColumnIds: number[],
    public readonly setIngestion: (ingestion: ImportModalCsvTypes.Ingestion) => void,
    public readonly setIngestionColumns: (columns: ImportModalCsvTypes.IngestionColumn[]) => void,
    public readonly setSelectedColumnIds: (columns: number[]) => void,
    public readonly changeStep: (step: ImportModalCsvTypes.IngestionSteps) => void,
    public readonly openCreditsModal: (project: ImportModalCsvTypes.ProjectProp) => void,
    public readonly isEmptyProject: boolean
  ) {
    super({ selectedColumnsIds: [], columns: [], baseColumns: [] });
  }

  public back = () => {
    this.setSelectedColumnIds(this.currentState('selectedColumnsIds'));
    this.changeStep('csv_received');
  };

  private formatColumnsTosave = () =>
    this.$selectedColumnsIds.pipe(
      map((selectedColumnIds) =>
        this.currentState('baseColumns').map((col) => ({
          ...col,
          tcEnabled: !!selectedColumnIds.includes(col.id),
        }))
      ),
      tap((columns) => this.setIngestionColumns(columns)),
      map((columns) =>
        columns.map(({ id, customName, ignoreColumn, tcEnabled, responseDataTypeId, extraInfo }) => ({
          id,
          customName,
          ignoreColumn,
          tcEnabled,
          responseDataTypeId,
          extraInfo,
        }))
      )
    );

  public commitChanges = (next: boolean, onSuccess?: () => void) => {
    this.setState('loading', true);
    this.addSub(
      this.formatColumnsTosave()
        .pipe(
          switchMap((columns) =>
            combineLatest([
              $put<ImportModalCsvTypes.Ingestion>(`ingestions/${this.currentIngestionId}/set-tc`, {
                mapping: columns,
                goToNextStep: next,
              }).pipe(finalize(() => this.setState('loading', false))),
              this.$costValues,
            ])
          )
        )
        .pipe(first())
        .subscribe(([ingestion, costValues]) => {
          this.setIngestion(ingestion);
          this.setSelectedColumnIds(this.currentState('selectedColumnsIds'));
          next &&
            this.openCreditsModal({
              amount: costValues?.total || 0,
              id: ingestion?.id || '',
              name: ingestion?.name || '',
              type: 'ingestion',
            });
          onSuccess?.();
        }),
      'commitChanges'
    );
  };

  public updateCostDetails = () => {
    this.setState('updateCostDetailsLoading', true);
    this.addSub(
      this.formatColumnsTosave()
        .pipe(
          switchMap((columns) =>
            $put<ImportModalCsvTypes.Ingestion>(`ingestions/${this.currentIngestionId}/set-tc`, {
              mapping: columns,
              goToNextStep: false,
            }).pipe(
              switchMap(() =>
                $get<ImportModalCsvTypes.CostDetails>(
                  `ingestions/${this.currentIngestionId}/cost-details`
                ).pipe(finalize(() => this.setState('updateCostDetailsLoading', false)))
              )
            )
          )
        )
        .subscribe({
          next: (data) => this.setState('costDetails', data),
        }),
      'updateCostDetails'
    );
  };

  public handleUseLegacyCodeframes = (value?: boolean) => this.setState('useLegacyCodeframes', value);

  public handleChangeCheckbox = (e: CheckboxChangeEvent, id: number) =>
    e.checked ? this.selectColunm(id) : this.unselectColunm(id);

  public selectColunm = (id: number) =>
    this.mutateState('selectedColumnsIds', (prevState) => [...prevState, id]);

  public unselectColunm = (id: number) => {
    this.mutateState('selectedColumnsIds', (prevState) => [...prevState].filter((col) => col !== id));
    this.handleChangePipelineId(id, null);
  };

  public handleChangeMainCheckbox = (e: CheckboxChangeEvent) =>
    e.checked ? this.selectAll() : this.unselectAll();

  public selectAll = () => {
    this.addSub(
      this.$filteredColumn.pipe(first()).subscribe((columns) => {
        this.setState(
          'selectedColumnsIds',
          columns.map((col) => col.id)
        );
        this.unsub('selectAll');
      }),
      'selectAll'
    );
  };

  public unselectAll = () => {
    this.setState('selectedColumnsIds', []);
    this.addSub(
      this.$columns.pipe(first()).subscribe((columns) => {
        columns.forEach((col) => this.handleChangePipelineId(col.id, null));
        this.unsub('unselectAll');
      }),
      'unselectAll'
    );
  };

  public handleChangePipelineId = (id: number, value: number | null) => {
    this.mutateState('columns', (prevState) => {
      let tempColumns = prevState.slice();
      const i = tempColumns.findIndex((ing) => ing.id === id);
      tempColumns[i].extraInfo.recommendedPipelineId = value;
      tempColumns[i].extraInfo.recommendedCodeframeId = null;
      tempColumns[i].extraInfo.tcCodeframeId = null;
      return tempColumns;
    });
    this.updateCostDetails();
  };

  public handleChangeCodeFrameId = (id: number, value: number | null) => {
    this.mutateState('columns', (prevState) => {
      let tempColumns = prevState.slice();
      const i = tempColumns.findIndex((ing) => ing.id === id);
      tempColumns[i].extraInfo.recommendedCodeframeId = value;
      return tempColumns;
    });
    this.updateCostDetails();
  };

  public handleChangeTcCodeframeId = (id: number, value: number | null) => {
    this.mutateState('columns', (prevState) => {
      let tempColumns = prevState.slice();
      const i = tempColumns.findIndex((ing) => ing.id === id);
      tempColumns[i].extraInfo.tcCodeframeId = value;
      tempColumns[i].extraInfo.recommendedPipelineId = null;
      tempColumns[i].extraInfo.recommendedCodeframeId = null;
      return tempColumns;
    });
    this.updateCostDetails();
  };

  private filterTextResponseColumns(
    columns: ImportModalCsvTypes.IngestionColumn[]
  ): ImportModalCsvTypes.IngestionColumn[] {
    return columns.filter(
      (col) => !col.ignoreColumn && col.responseDataTypeId === TC_IMPORT_TEXT_RESPONSE_TYPE_ID
    );
  }

  private addCostDataToColumns(
    columns: ImportModalCsvTypes.IngestionColumn[],
    costDetails: ImportModalCsvTypes.CostDetails
  ): Column[] {
    return columns.map((c) => {
      const columnCosts = costDetails?.tc.columns.find((col) => col.id === c.id);
      const realCost = this.getColumnRealCost(c.id, costDetails);

      return {
        ...c,
        costData: {
          hasHistoricTc: columnCosts?.cost.hasHistoricTc || false,
          realCost,
          count: columnCosts?.cost.ownResponsesCount || 0,
          colName: c.customName || columnCosts?.name || '',
        },
      };
    });
  }

  public getColumnRealCost = (columnId: number, costDetails?: ImportModalCsvTypes.CostDetails) => {
    const columnCosts = costDetails?.tc.columns.find((col) => col.id === columnId);
    return columnCosts?.cost.hasHistoricTc
      ? columnCosts?.cost.ownResponsesCost
      : columnCosts?.cost.totalCost || 0;
  };
}

const Context = React.createContext<Readonly<BLoC>>({} as any);

export const useHeyYabbleCountStepBLoC = () => useBLoC<BLoC>(Context);

export const HeyYabbleCountStepBLoC: React.FC<BLoCParams<BLoC, State>> = ({ children }) => {
  const {
    $currentIngestion,
    $currentIngestionColumns,
    $currentSelectedColumnIds,
    setIngestion,
    setIngestionColumns,
    changeStep,
    setSelectedColumnIds,
    openCreditsModal,
    isEmptyProject,
  } = useImportModalCsvBLoC();

  const ingestion = useObservableState($currentIngestion.pipe(first()));
  const columns = useObservableState($currentIngestionColumns.pipe(first()), []);
  const selectedColumnIds = useObservableState($currentSelectedColumnIds.pipe(first()), []);
  const selectedIds = selectedColumnIds?.length
    ? selectedColumnIds
    : columns
        .filter(
          (col) =>
            col.tcEnabled && !col.ignoreColumn && col.responseDataTypeId === TC_IMPORT_TEXT_RESPONSE_TYPE_ID
        )
        .map(({ id }) => id);

  const bloc = useInitBloc(
    () =>
      ingestion && columns
        ? new BLoC(
            ingestion.id,
            columns,
            selectedIds,
            setIngestion,
            setIngestionColumns,
            setSelectedColumnIds,
            changeStep,
            openCreditsModal,
            isEmptyProject || false
          )
        : null,
    [ingestion, columns, selectedColumnIds]
  );
  return bloc ? <Context.Provider value={bloc}>{renderBlocChild(children, bloc)}</Context.Provider> : null;
};
